import React, { Dispatch, useEffect, useMemo, useRef, useState } from "react"

import { ReactComponent as IconBulb } from "@common_assets/svg/bulb.svg"
import { ReactComponent as SvgPhoto } from "@common_assets/svg/photo.svg"
import { ReactComponent as IconQuestionMark } from "@common_assets/svg/question.svg"
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import { useQueryShortClassList } from "api/reactQuery/queries/shortClassList"
import { IShortClassStudent } from "api/reactQuery/queries/shortClassList.types"
import ErrorText from "components/common/error/errorText"
import IconWrapper from "components/common/icon/iconWrapper"
import ListingSortingHeader from "components/common/listing/listingSortingHeader"
import NoElementsMessage from "components/common/listing/noElementsMessage"
import NoMatchesForSearch from "components/common/listing/noMatchesForSearch"
import TableSkeleton from "components/common/skeleton/tableSkeleton"
import { PrimaryTile } from "components/common/tile"
import SearchbarForm from "components/form/searchbarForm"
import { Action } from "layout/asideNav/navigation/asideOptions/classAside/ClassAside.utility"
import { useTranslation } from "react-i18next"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { getSortingTextSecond } from "utils/listing"

import {
  sortingOptions,
  sortingTextMap,
  Sort,
  SortingType,
  STUDENTS_PAGE_SIZE,
} from "./Advance.utility"
import { useSortedStudents } from "./hook"

interface AdvanceProps {
  classId?: number
  dispatchState?: Dispatch<Action>
  onDirtyChange?: (isDirty: boolean) => void
}

const Advance = ({ classId, dispatchState, onDirtyChange }: AdvanceProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [sorting, setSorting] = useState<SortingType>("asc")
  const [showNoMatches, setShowNoMatches] = useState(false)
  const [searchFilter, setSearchFilter] = useState("")
  const [sortBy, setSortBy] = useState<Sort>("first_name")
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [savedMathIds, setSavedMathIds] = useState<number[]>([])
  const isSelectionInitialized = useRef(false)
  const [visibleCount, setVisibleCount] = useState(STUDENTS_PAGE_SIZE)

  const {
    data: studentsData,
    isLoading,
    isFetching,
    isError,
  } = useQueryShortClassList({
    classId: classId as number,
    // The whole class is fetched once, search and sort run client-side,
    // so the selection always covers every student of the class
    limit: 0,
    options: {
      cacheTime: 0,
      enabled: !!classId,
      onSuccess: (data) => {
        const rosterIds = data.data.objects.map((item) => item.id)
        const mathIds = data.data.objects
          .filter((item) => item.is_math_enabled)
          .map((item) => item.id)
        setSavedMathIds(mathIds)
        // The first response seeds the selection. Later responses keep unsaved
        // ticks, but drop students who left the class.
        if (!isSelectionInitialized.current) {
          isSelectionInitialized.current = true
          setSelectedIds(mathIds)
        } else {
          setSelectedIds((current) =>
            current.filter((id) => rosterIds.includes(id)),
          )
        }

        // Math settings are saved only once the students have loaded
        dispatchState &&
          classId &&
          dispatchState({
            type: "classID",
            classID: classId,
          })
      },
    },
  })

  const allStudents = studentsData?.data.objects

  const filteredStudents = useMemo(() => {
    const query = searchFilter.trim().toLowerCase()
    if (!allStudents) return []
    if (!query) return allStudents
    return allStudents.filter((item) =>
      `${item.first_name} ${item.last_name}`.toLowerCase().includes(query)
    )
  }, [allStudents, searchFilter])

  const students = useSortedStudents({
    students: filteredStudents,
    sortBy,
    sortOrder: sorting,
  })

  const getIsChecked = (student: IShortClassStudent) =>
    selectedIds.includes(student.id)

  const toggleItem = (student: IShortClassStudent) => {
    if (getIsChecked(student)) {
      setSelectedIds(selectedIds.filter((id) => id !== student.id))
    } else {
      setSelectedIds([...selectedIds, student.id])
    }
  }

  // The header checkbox acts on the students matching the search
  const areAllVisibleChecked = !!students.length && students.every(getIsChecked)
  const isSomeVisibleChecked = students.some(getIsChecked)

  const toggleAllVisible = () => {
    const visibleIds = students.map((student) => student.id)
    if (areAllVisibleChecked) {
      setSelectedIds(selectedIds.filter((id) => !visibleIds.includes(id)))
    } else {
      setSelectedIds([
        ...selectedIds,
        ...visibleIds.filter((id) => !selectedIds.includes(id)),
      ])
    }
  }

  const displayMathView = !selectedIds.length

  useEffect(() => {
    if (!isLoading && !!searchFilter.length && !students.length) {
      const timer = setTimeout(() => {
        setShowNoMatches(true)
      }, 500)

      return () => clearTimeout(timer)
    } else {
      setShowNoMatches(false)
    }
  }, [isLoading, searchFilter, students])

  useEffect(() => {
    dispatchState && dispatchState({ type: "studentsFetching", isFetching })

    return () => {
      dispatchState &&
        dispatchState({ type: "studentsFetching", isFetching: false })
    }
  }, [isFetching])

  const isDirty =
    selectedIds.length !== savedMathIds.length ||
    selectedIds.some((id) => !savedMathIds.includes(id))

  useEffect(() => {
    onDirtyChange && onDirtyChange(isDirty)
  }, [isDirty])

  useEffect(() => {
    return () => {
      onDirtyChange && onDirtyChange(false)
    }
  }, [])

  useEffect(() => {
    dispatchState &&
      dispatchState({
        type: "enableCheckedStudents",
        studentArr: selectedIds,
      })
  }, [selectedIds])

  return (
    <Stack pt={2} minHeight={0}>
      <Typography variant="subtitle2">Math calculations</Typography>
      <Stack
        direction="row"
        columnGap={1}
        sx={{
          pl: 3,
          mt: 1,
        }}
      >
        <Typography
          variant="body1"
          fontWeight="500"
          color={theme.palette.mockup.grey70}
        >
          {t("addNewClass.manageMathCalculationsForStudents")}
        </Typography>
        <Tooltip
          title={t(
            "addNewClass.checkedStudentsWillBeResponsibleForDoingTheMath"
          )}
        >
          <IconQuestionMark tabIndex={0} />
        </Tooltip>
      </Stack>
      <PrimaryTile
        mt={2}
        display="flex"
        flexDirection="column"
        minHeight={0}
      >
        <Stack p={2}>
          <SearchbarForm
            onSubmit={(data) => {
              setSearchFilter(data.search)
              setVisibleCount(STUDENTS_PAGE_SIZE)
            }}
            placeholder={`${t("store.searchForItem")}...`}
            renderAsForm={false}
          />
        </Stack>
        {/* Both bars share one grid cell, so swapping them doesn't change the height */}
        <Box display="grid">
          <Stack
            aria-hidden={!displayMathView}
            sx={{
              gridArea: "1 / 1",
              visibility: displayMathView ? "visible" : "hidden",
              borderTop: `1px solid ${theme.palette.mockup.primary_base}`,
              borderBottom: `1px solid ${theme.palette.mockup.primary_base}`,
              flexDirection: "row",
              alignItems: "center",
              p: 2,
            }}
          >
            <Typography sx={{ whiteSpace: "nowrap" }}>
              {t("students.mathOn")}
            </Typography>
            <Box display="flex" alignItems="center" pl={2}>
              <IconWrapper width="14px" height="20px" sx={{ mr: 1 }}>
                <IconBulb width="14px" height="14px" viewBox="0 0 14 14" />
              </IconWrapper>
              <Typography fontWeight="500">
                {t(
                  "students.dontWantStudentsToHaveToAddOrSubtractTheirTransactions"
                )}
              </Typography>
            </Box>
          </Stack>
          <Box
            display="flex"
            aria-hidden={displayMathView}
            sx={{
              gridArea: "1 / 1",
              visibility: displayMathView ? "hidden" : "visible",
              alignItems: "center",
              borderBottom: `1px solid ${theme.palette.mockup.primary_base}`,
              justifyContent: "space-between",
              backgroundColor: theme.palette.mockup.primary95,
              p: 2,
            }}
          >
            <Typography variant="body2">Math On</Typography>
            <Typography>
              {`${selectedIds.length} students selected`}
            </Typography>
          </Box>
        </Box>
        <ListingSortingHeader
          onSubmit={(data) => {
            const dataSortReturn = JSON.parse(data.sort)
            const { order, orderBy } = dataSortReturn
            setSortBy(orderBy)
            setSorting(order)
            setVisibleCount(STUDENTS_PAGE_SIZE)
          }}
          checkboxProps={{
            checked: areAllVisibleChecked,
            onChange: toggleAllVisible,
            indeterminate: isSomeVisibleChecked && !areAllVisibleChecked,
          }}
          sortingDirection={sorting}
          options={sortingOptions}
          sortingActive={!!sortBy}
          defaultRender={sortBy}
          sortingText={t(getSortingTextSecond(sortBy, sortingTextMap))}
        />
        <Box
          sx={{
            // Grows to the viewport, then shrinks to the space left in the dialog
            flex: "1 1 100dvh",
            minHeight: "200px",
            overflowY: isLoading ? "hidden" : "auto",
          }}
        >
          {isLoading && <TableSkeleton />}
          {isError && <ErrorText />}
          {!isLoading && showNoMatches && (
            <NoMatchesForSearch search={searchFilter} />
          )}
          {!isLoading && !searchFilter.length && !studentsData?.data.objects?.length && (
            <NoElementsMessage
              boxStyle={{ py: { mobile: "16px", tablet: "62px" } }}
              Icon={<SvgPhoto />}
              title={t("students.youDoNotHaveAnyStudentsYet")}
              description={t("students.addStudentsManuallyOrHaveStudents")}
            />
          )}
          {!isLoading && (
            <>
              <TransitionGroup component={null}>
                {students.slice(0, visibleCount).map((item) => (
                  <CSSTransition
                    key={item.id}
                    timeout={500}
                    classNames="animation-fade"
                    unmountOnExit
                  >
                    <Box
                      display="flex"
                      key={item.id}
                      alignItems="center"
                      sx={{
                        ":nth-of-type(even)": {
                          backgroundColor: theme.palette.mockup.primary99,
                        },
                      }}
                    >
                      <FormControl sx={{ py: 2, pl: 1, pr: 3 }}>
                        <Checkbox
                          checked={getIsChecked(item)}
                          onChange={() => {
                            toggleItem(item)
                          }}
                          sx={{ width: "24px", height: "24px" }}
                        />
                      </FormControl>
                      <Typography>{`${item.first_name} ${item.last_name}`}</Typography>
                    </Box>
                  </CSSTransition>
                ))}
              </TransitionGroup>
              {students.length > visibleCount && (
                <Box display="flex" justifyContent="center" py={1}>
                  <Button
                    variant="text"
                    onClick={() =>
                      setVisibleCount((prev) => prev + STUDENTS_PAGE_SIZE)
                    }
                  >
                    {t("addNewClass.showMore")}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </PrimaryTile>
    </Stack>
  )
}
export default Advance
